/**
 * Cascaron.tsx — la barra lateral, la barra superior y el hueco donde va cada
 * pantalla. Es el mockup de la Fase 0, ya como componente.
 *
 * Dos cosas que el mockup no tenía y el producto sí necesita:
 *
 * 1. **El conmutador de idioma es del producto, no de la demo.** En los
 *    mockups vivía en una barra negra de andamio. Acá es un control de la
 *    aplicación, porque el idioma decide cómo se lee un monto tecleado.
 *
 * 2. **El chip del tipo de cambio dice su FUENTE.** Un tipo `copiado_del_anterior`
 *    no respalda un comprobante fiscal, así que la pantalla no puede mostrarlo
 *    igual que uno declarado vigente. El mockup mostraba el número a secas —
 *    que es exactamente la forma de que nadie se entere.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { FronteraDeError } from './FronteraDeError.tsx';
import { useIdioma } from '../nucleo/idioma.tsx';
import { anioYMesDeHoy, hoy } from '../nucleo/hoy.ts';
import type { Clave } from '../nucleo/idioma.ts';
import {
  IconoAsientos, IconoConfiguracion, IconoFacturas, IconoIntegraciones,
  IconoReportes, IconoTablero, IconoUsuarios, Isotipo, type Icono,
} from './Iconos.tsx';
import {
  traerPasosDelCierre, traerTipoDeCambio, type TipoDelDia,
} from '../datos/libros.ts';
import { useSesion } from '../nucleo/sesion.tsx';
import {
  alCambiarInstalacion, instalar, sePuedeInstalar, yaInstalada,
} from '../nucleo/pwa.ts';

interface Entrada {
  readonly a: string;
  readonly clave: Clave;
  readonly Icono: Icono;
  readonly insignia?: number;
}

const PRINCIPAL: readonly Entrada[] = [
  { a: '/', clave: 'nav.dashboard', Icono: IconoTablero },
];

const CONTABILIDAD: readonly Entrada[] = [
  { a: '/asientos', clave: 'nav.entries', Icono: IconoAsientos },
  { a: '/diario', clave: 'jr.title', Icono: IconoAsientos },
  { a: '/catalogo', clave: 'cat.title', Icono: IconoReportes },
  { a: '/contrapartes', clave: 'cp.title', Icono: IconoUsuarios },
  { a: '/por-pagar', clave: 'doc.title', Icono: IconoFacturas },
  { a: '/por-cobrar', clave: 'cob.title', Icono: IconoFacturas },
  { a: '/apertura', clave: 'ap.title', Icono: IconoAsientos },
  { a: '/activos', clave: 'act.title', Icono: IconoReportes },
  { a: '/diferidos', clave: 'dif.title', Icono: IconoReportes },
  { a: '/proyectos', clave: 'pry.title', Icono: IconoReportes },
  { a: '/banco', clave: 'ban.title', Icono: IconoReportes },
  { a: '/cierre', clave: 'cierre.title', Icono: IconoReportes },
  { a: '/facturas', clave: 'nav.invoices', Icono: IconoFacturas },
  { a: '/reportes', clave: 'nav.reports', Icono: IconoReportes },
];

const SISTEMA: readonly Entrada[] = [
  { a: '/bitacora', clave: 'bit.title', Icono: IconoAsientos },
  { a: '/integraciones', clave: 'nav.integrations', Icono: IconoIntegraciones },
  { a: '/usuarios', clave: 'nav.users', Icono: IconoUsuarios },
  { a: '/configuracion', clave: 'nav.settings', Icono: IconoConfiguracion },
];

function Item({ a, clave, Icono, insignia }: Entrada) {
  const { t } = useIdioma();
  return (
    <NavLink
      to={a}
      end={a === '/'}
      className={({ isActive }) => `nav__item${isActive ? ' is-active' : ''}`}
    >
      <Icono />
      <span>{t(clave)}</span>
      {insignia !== undefined && <em className="nav__badge">{insignia}</em>}
    </NavLink>
  );
}

function Grupo({ rotulo, entradas }: { rotulo: Clave; entradas: readonly Entrada[] }) {
  const { t } = useIdioma();
  return (
    <div className="nav__group">
      <div className="nav__group-label">{t(rotulo)}</div>
      {entradas.map((e) => <Item key={e.a} {...e} />)}
    </div>
  );
}

function BarraLateral() {
  const { t } = useIdioma();
  // La empresa sale de la SESION, no de los datos de muestra. Es el primer
  // dato de esta pantalla que dejo de ser inventado.
  const { sesion, cambiarEmpresa, empresas } = useSesion();
  // ▶ Sin sesión no se inventa una razón social.
  //
  // Decía `?? EMPRESA.razonSocial`, o sea el nombre de la empresa de EJEMPLO.
  // Una barra lateral que dice «SkopedLabs S.A.» cuando todavía no se sabe en
  // qué empresa se entró es una afirmación sobre algo que nadie eligió.
  const razon = sesion?.empresa.razonSocial ?? '';
  const marca = razon.slice(0, 2).toUpperCase();
  const rol = sesion
    ? t((sesion.rol === 'solo_lectura' ? 'common.readonly' : `common.${sesion.rol}`) as Clave)
    : '';

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <Isotipo className="iso" tono="oscuro" />
        <span className="wordmark wordmark--dark" style={{ fontSize: 17 }}>
          Skoped <span>Books</span>
        </span>
      </div>

      <nav className="nav" aria-label={t('nav.main')}>
        {PRINCIPAL.map((e) => <Item key={e.a} {...e} />)}
        <Grupo rotulo="nav.group.accounting" entradas={CONTABILIDAD} />
        <Grupo rotulo="nav.group.system" entradas={SISTEMA} />
      </nav>

      <div className="sidebar__foot">
        <button
          className="company-switch" type="button"
          onClick={cambiarEmpresa}
          disabled={empresas.length < 2}
          title={empresas.length > 1 ? t('acc.switchCompany') : undefined}
        >
          <span className="company-switch__mark">{marca}</span>
          <span style={{ minWidth: 0 }}>
            <span className="company-switch__name">{razon}</span><br />
            <span className="company-switch__meta">
              {rol}{sesion?.capacidad === 'piso' ? ` · ${t('acc.floor')}` : ''}
            </span>
          </span>
        </button>
      </div>
    </aside>
  );
}

/**
 * El chip del tipo de cambio.
 *
 * Muestra la fuente porque de ella depende si el número sirve para un
 * documento con consecuencia fiscal. Un valor copiado se marca en ámbar: no
 * es un error —el sistema sigue funcionando— pero tampoco puede pasar por un
 * dato declarado.
 */
function ChipTipoDeCambio() {
  const { t, fecha, monto } = useIdioma();
  const { pedir, sesion } = useSesion();
  const [tipo, setTipo] = useState<TipoDelDia | null>(null);
  const [pedido, setPedido] = useState(false);

  // ▶ El tipo del día sale de la RÉPLICA. Este chip mostraba un fixture, y el
  // día que la réplica quedó vacía siguió diciendo «1 USD = ₡ 512,45» con toda
  // naturalidad. Un número inventado en la cabecera es peor que ninguno: nadie
  // lo comprueba, porque para eso está el chip.
  useEffect(() => {
    let vivo = true;
    const dia = hoy();
    (async () => {
      try {
        const t2 = await traerTipoDeCambio(pedir, 'USD', dia);
        if (vivo) { setTipo(t2); setPedido(true); }
      } catch {
        if (vivo) { setTipo(null); setPedido(true); }
      }
    })();
    return () => { vivo = false; };
  }, [pedir]);

  // Mientras no se sabe, no se dice nada. Un chip que aparece con un número y
  // lo cambia es peor que uno que aparece cuando tiene el dato.
  if (!pedido) return null;

  // Sin tipo, se dice que no hay. Callarlo dejaría la cabecera igual que
  // cuando sí lo hay y todo estaba bien.
  if (!tipo) {
    return <span className="chip chip--warn" title={t('fx.none')}>USD · —</span>;
  }

  // ▶ Se pregunta por lo que SÍ respalda, no por lo que no.
  //
  // Decía `fuente === 'copiado_del_anterior'`, así que cualquier tercer valor
  // que el hub llegue a mandar caería del lado tranquilizador: el aviso diría
  // «declarado vigente por el BCCR» sobre un tipo que nadie declaró. Al revés,
  // un valor desconocido queda del lado prudente, que es donde tiene que caer
  // un dato con consecuencia fiscal.
  const respalda = tipo.fuente === 'vigente_declarado';
  // La moneda de la empresa en sesión, no la de `datos/muestra.ts`: ver el
  // comentario de `Reportes.tsx`. Sin sesión no se dibuja este chip.
  const venta = { valor: tipo.venta, moneda: sesion?.empresa.monedaLocal ?? '' };

  return (
    <span
      className={respalda ? 'chip chip--acc' : 'chip chip--warn'}
      title={`${t(respalda ? 'fx.declared' : 'fx.copied')} · ${fecha(tipo.fecha)}`}
    >
      1 {tipo.moneda} = {monto(venta)}
    </span>
  );
}

/**
 * ▶ Si el mes en curso está abierto o cerrado, PREGUNTÁNDOLO.
 *
 * Salía de `datos/muestra.ts`, así que decía «abierto» siempre — incluido un
 * mes que alguien acababa de cerrar. Un rótulo que no puede decir lo contrario
 * no informa de nada, y encima da permiso: quien lo lee cree que puede
 * registrar.
 *
 * La fuente es el paso 10 del cierre, que es el que bloquea el periodo. Se
 * pregunta a la misma función que dibuja la pantalla de cierre para que las
 * dos no puedan discrepar. Si el periodo no existe en el ejercicio, la base
 * responde con su código y acá no se dibuja nada — igual que el chip del tipo
 * de cambio cuando no hay tipo.
 */
function ChipDelPeriodo() {
  const { t } = useIdioma();
  const { pedir, sesion } = useSesion();
  const [cerrado, setCerrado] = useState<boolean | null>(null);

  // ▶ CUÁL periodo, y no sólo su estado.
  //
  // El chip decía «Periodo abierto» a secas. «Abierto» sin decir cuál es media
  // frase: quien lo lee supone que habla del mes que está mirando.
  //
  // ▶ Y el mes que importa es el de TRABAJO —el abierto más viejo sin cerrar—,
  // no el del calendario, igual que los selectores de las pantallas (0150). En
  // noviembre, con setiembre sin cerrar, el chip habla de setiembre, que es
  // donde está el trabajo pendiente.
  const [periodo, setPeriodo] = useState('');

  const empresaId = sesion?.empresa.id ?? null;
  const trabajo = sesion?.mesDeTrabajo ?? null;
  const tAnio = trabajo?.anio ?? null;
  const tMes = trabajo?.mes ?? null;

  useEffect(() => {
    if (!empresaId) { setCerrado(null); return; }
    let vivo = true;
    // El mes de trabajo de la sesión; si no lo hay (empresa sin periodos), el
    // calendario, como caían antes los selectores.
    const enCurso = tAnio !== null && tMes !== null
      ? { anio: tAnio, mes: tMes }
      : anioYMesDeHoy();
    (async () => {
      try {
        const pasos = await traerPasosDelCierre(pedir, enCurso.anio, enCurso.mes);
        if (!vivo) return;
        setPeriodo(`${String(enCurso.mes).padStart(2, '0')}-${enCurso.anio}`);
        const diez = pasos.find((x) => x.paso === 10);
        setCerrado(diez ? diez.estado === 'cumplido' : null);
      } catch {
        // Sin periodo, sin permiso o sin red: no se dibuja. Un chip que
        // adivina es peor que uno que no está.
        if (vivo) setCerrado(null);
      }
    })();
    return () => { vivo = false; };
  }, [pedir, empresaId, tAnio, tMes]);

  if (cerrado === null) return null;

  return (
    <span className={cerrado ? 'chip' : 'chip chip--ok'}>
      {t(cerrado ? 'dash.period.closed' : 'dash.period.open', { periodo })}
    </span>
  );
}

/**
 * ▶ El botón de instalar, y por qué es un botón.
 *
 * Con el manifiesto y el trabajador de servicio el navegador YA puede ofrecer
 * instalar — en un ícono de la barra de direcciones que hay que conocer, o en
 * una tira de Android que se cierra sola. O sea que sin esto «se puede
 * instalar» es cierto y nadie lo sabe.
 *
 * No aparece si ya está instalada, y no aparece en iOS: Safari no implementa el
 * evento. Eso último no es un defecto de acá y conviene no perseguirlo.
 */
function BotonDeInstalar() {
  const { t } = useIdioma();
  const [puede, setPuede] = useState(sePuedeInstalar());
  const [instalada, setInstalada] = useState(yaInstalada());

  useEffect(() => alCambiarInstalacion((p) => {
    setPuede(p);
    setInstalada(yaInstalada());
  }), []);

  if (instalada || !puede) return null;

  return (
    <button
      className="btn btn--ghost btn--sm" type="button"
      title={t('ver.instalarPor')}
      onClick={() => void instalar().then(() => setInstalada(yaInstalada()))}
    >
      {t('ver.instalar')}
    </button>
  );
}

function ConmutadorDeIdioma() {
  const { idioma, cambiar } = useIdioma();
  return (
    <div className="seg" role="group" aria-label="Idioma / Language">
      <button type="button" aria-pressed={idioma === 'es'} onClick={() => cambiar('es')}>ES</button>
      <button type="button" aria-pressed={idioma === 'en'} onClick={() => cambiar('en')}>EN</button>
    </div>
  );
}

/** El título de la barra superior sale de la ruta activa. */
function tituloDe(ruta: string): Clave {
  const todas = [...PRINCIPAL, ...CONTABILIDAD, ...SISTEMA];
  const exacta = todas.find((e) => e.a === ruta);
  if (exacta) return exacta.clave;
  const prefijo = todas.find((e) => e.a !== '/' && ruta.startsWith(e.a));
  return prefijo?.clave ?? 'nav.dashboard';
}

/** Las iniciales de quien entro, sacadas de su correo. Al pulsarlo, sale. */
function Avatar() {
  const { correo, salir } = useSesion();
  return (
    <button className="avatar" type="button" title={correo ?? ''} onClick={() => void salir()}>
      {(correo ?? '?').slice(0, 2).toUpperCase()}
    </button>
  );
}

function BarraSuperior() {
  const { t } = useIdioma();
  const { pathname } = useLocation();
  const { sesion } = useSesion();

  return (
    <header className="topbar">
      <span className="topbar__title">{t(tituloDe(pathname))}</span>
      <span className="topbar__path">skoped-books:/{pathname === '/' ? '/tablero' : pathname}</span>
      <span className="topbar__spacer" />
      <ConmutadorDeIdioma />
      <BotonDeInstalar />
      <ChipTipoDeCambio />
      {/* El piso SE VE. Una capacidad que solo existiera como un boton
          deshabilitado no se entiende: la persona tiene que saber por que no
          puede registrar, y que sus periodos cerrados si los tiene. */}
      {sesion?.capacidad === 'piso' && (
        <span className="chip chip--warn" title={t('acc.floorWhy')}>{t('acc.floor')}</span>
      )}
      <ChipDelPeriodo />
      <Avatar />
    </header>
  );
}

export function Cascaron() {
  const { pathname } = useLocation();
  const { proponer } = useIdioma();
  const idiomaDeLaEmpresa = useSesion().sesion?.empresa.idiomaPorOmision ?? null;

  /* ▶ El idioma de la EMPRESA, propuesto — no impuesto.
   *
   * Va acá porque es el primer sitio donde ya hay una empresa elegida, y se
   * llama `proponer` y no `cambiar` a propósito: si esta persona tocó alguna vez
   * el conmutador de arriba, su preferencia manda y esto no hace nada.
   *
   * Imponerlo sería peor que no tenerlo: un contador que puso Books en inglés lo
   * vería volver al español cada vez que entra, sin saber por qué. */
  useEffect(() => {
    if (idiomaDeLaEmpresa) proponer(idiomaDeLaEmpresa);
  }, [idiomaDeLaEmpresa, proponer]);

  return (
    <div className="app">
      <BarraLateral />
      <div className="main">
        <BarraSuperior />
        <main className="content">
          {/* ▶ La frontera envuelve el CONTENIDO y no la aplicación, para que
              una pantalla rota deje el menú vivo: lo que falla es una pantalla y
              todas las demás siguen a un clic.

              Y la `key` con la ruta no es cosmética — es lo que hace que la
              frontera se reponga al navegar. Sin ella, un fallo en una pantalla
              se queda pegado y convierte un defecto en una sesión perdida.

              Sin esto, un render que lanza deja la página EN BLANCO: ni mensaje
              ni código. Pasó de verdad el 11 de setiembre. Ver
              `componentes/FronteraDeError.tsx`. */}
          <FronteraDeError key={pathname}>
            <Outlet />
          </FronteraDeError>
        </main>
      </div>
    </div>
  );
}
